import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/Card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/Table";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { formatNumber, formatDecimal, formatPercent, formatMonth } from "./format";
import { downloadSectionedXlsx } from "./xlsxExport";
import { KpiCard, TablePager, usePagedRows } from "./ui";

const UNMAPPED_VISN = "Unmapped";
const UNKNOWN_SPECIALTY = "Unknown";

// "How loyal are providers staying with us" is a magnitude comparison across
// a VISN x specialty grid -> heatmap, sequential color job (per dataviz's
// choosing-a-form: "Compare magnitude, low -> high ... heatmap for a grid ->
// sequential (one hue)"). Longest consecutive streak is the primary loyalty
// signal (continuity, not just total tenure), so it drives cell color; the
// actual number is always printed inside the cell too — a WARN-worthy low
// contrast at the light end of the ramp is never the only way to read a
// value, per the contrast check's relief-channel requirement. Fill uses
// bg-purple-500 rather than the theme's bg-primary token — bg-primary
// rendered fully invisible in this build (see RetentionTab's chart, same
// underlying issue), so this uses a concrete Tailwind color that doesn't
// depend on it.
const HEATMAP_OPACITY_FLOOR = 0.12;

function axisByLoyalty(heatmap, labelOf, fallback) {
  const by = new Map();
  for (const cell of heatmap) {
    const label = labelOf(cell);
    const users = cell.provider_count || 0;
    const current = by.get(label) || { users: 0, streakSum: 0 };
    current.users += users;
    current.streakSum += (cell.avg_longest_streak_months || 0) * users;
    by.set(label, current);
  }
  return [...by.keys()].sort((a, b) => {
    if (a === fallback) return 1;
    if (b === fallback) return -1;
    const score = (label) => {
      const row = by.get(label);
      return row.users ? row.streakSum / row.users : 0;
    };
    return score(b) - score(a) || String(a).localeCompare(String(b));
  });
}

function LoyaltyHeatmap({ heatmap }) {
  const visns = axisByLoyalty(
    heatmap,
    (cell) => (cell.visn == null ? UNMAPPED_VISN : String(cell.visn)),
    UNMAPPED_VISN
  );
  const specialties = axisByLoyalty(
    heatmap,
    (cell) => cell.specialty || UNKNOWN_SPECIALTY,
    UNKNOWN_SPECIALTY
  );

  const cellByKey = new Map(
    heatmap.map((c) => [`${c.visn == null ? UNMAPPED_VISN : c.visn}::${c.specialty || UNKNOWN_SPECIALTY}`, c])
  );

  const maxStreak = Math.max(...heatmap.map((c) => c.avg_longest_streak_months || 0), 1);

  return (
    <div className="overflow-x-auto">
      <table className="border-collapse text-sm">
        <thead>
          <tr>
            {/* Corner cell freezes on both axes — it needs to stay put through
                both vertical and horizontal scroll, so it gets a higher
                z-index than the row-only/column-only sticky cells below,
                which would otherwise scroll over it. A solid bg is required
                on every sticky cell so scrolled-under content doesn't show
                through underneath it. */}
            <th className="sticky top-0 left-0 z-20 bg-white text-left text-xs font-medium text-muted-foreground p-2">
              VISN \ Specialty
            </th>
            {specialties.map((sp) => (
              <th
                key={sp}
                className="sticky top-0 z-10 bg-white text-center text-xs font-medium text-muted-foreground p-2 min-w-[92px]"
              >
                {sp}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visns.map((v) => (
            <tr key={v}>
              <td className="sticky left-0 z-10 bg-white text-xs font-medium text-muted-foreground p-2 whitespace-nowrap">
                {v === UNMAPPED_VISN ? v : `VISN ${v}`}
              </td>
              {specialties.map((sp) => {
                const cell = cellByKey.get(`${v}::${sp}`);
                if (!cell) {
                  return <td key={sp} className="p-1 text-center text-xs text-muted-foreground">—</td>;
                }
                const opacity =
                  HEATMAP_OPACITY_FLOOR +
                  (1 - HEATMAP_OPACITY_FLOOR) * (cell.avg_longest_streak_months / maxStreak);
                // At low opacity the surface behind the fill dominates (light),
                // so ink text stays legible; past the midpoint the fill itself
                // is dominant enough that white text clears contrast instead —
                // the "pick white or ink by the fill's luminance" rule,
                // approximated since this is an opacity ramp rather than a
                // literal lightness step.
                const textClass = opacity < 0.5 ? "text-foreground" : "text-white";
                const subtextClass = opacity < 0.5 ? "text-muted-foreground" : "text-white/80";
                return (
                  <td key={sp} className="p-1">
                    <div
                      className="rounded flex flex-col items-center justify-center gap-0.5 py-2 bg-purple-500"
                      style={{ opacity }}
                      title={`${v === UNMAPPED_VISN ? v : `VISN ${v}`} · ${sp}\n${formatNumber(
                        cell.provider_count
                      )} users\nAvg longest streak: ${formatDecimal(cell.avg_longest_streak_months, 1)} mo\nAvg months active: ${formatDecimal(
                        cell.avg_months_active,
                        1
                      )}\nCurrently active: ${formatPercent(cell.pct_currently_active)}`}
                    >
                      <span className={`text-xs font-semibold ${textClass}`}>
                        {formatDecimal(cell.avg_longest_streak_months, 1)} mo
                      </span>
                      <span className={`text-[10px] ${subtextClass}`}>{formatNumber(cell.provider_count)} usr</span>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Distribution bar charts — one bar per distinct value (not bucketed ranges,
// since these values are small discrete whole-month counts), bar height =
// number of providers at that value. Same bg-purple-500 fill as the heatmap
// and Retention's rating chart (bg-primary renders invisible in this build —
// see those components' comments). Wrapped in overflow-x-auto since the
// value range (e.g. up to 30+ months active) can run wider than the card.
const DISTRIBUTION_CHART_HEIGHT_PX = 120;

function DistributionChart({ distribution, unitLabel, sharedMaxCount }) {
  // sharedMaxCount lets two charts that should be compared bar-for-bar (see
  // the Longest streak / Potential streak pair below) scale against the
  // same ceiling instead of each normalizing to its own tallest bar — two
  // charts with different maxes both stretch their tallest bar to fill the
  // same pixel height, which makes two truly-different-sized populations
  // look identical at a glance. Falls back to this chart's own max when no
  // shared value is passed (the standalone Months active distribution).
  const maxCount = sharedMaxCount ?? Math.max(...distribution.map((d) => d.count), 1);
  return (
    <div className="overflow-x-auto">
      <div
        className="flex items-end gap-2"
        style={{ height: DISTRIBUTION_CHART_HEIGHT_PX + 44, minWidth: "fit-content" }}
      >
        {distribution.map((d) => {
          const barHeight = Math.max(Math.round((d.count / maxCount) * DISTRIBUTION_CHART_HEIGHT_PX), 3);
          return (
            <div
              key={d.value}
              className="flex flex-col items-center gap-1 min-w-[32px]"
              title={`${formatNumber(d.count)} user${d.count === 1 ? "" : "s"} at ${d.value} ${unitLabel}${
                d.value === 1 ? "" : "s"
              }`}
            >
              <span className="text-xs font-medium text-foreground">{formatNumber(d.count)}</span>
              <div className="w-full max-w-[28px] rounded-t bg-purple-500" style={{ height: `${barHeight}px` }} />
              <span className="text-xs text-muted-foreground whitespace-nowrap">{d.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EMPTY_PROVIDERS = [];

export default function LtvTab({ data, loading, error }) {
  const providers = data?.providers || EMPTY_PROVIDERS;
  const pager = usePagedRows(providers, { pageSize: 10 });

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading Customer LTV report…</p>;
  }
  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }
  if (!data) return null;

  const {
    total_count,
    returned_count,
    row_limit,
    summary,
    heatmap,
    unmapped_station_ids,
    streak_distribution,
    active_span_distribution,
    months_active_distribution,
  } = data;

  // Shared ceiling for the Longest streak / Potential streak span pair
  // (rendered side by side below) — both charts scale against the taller
  // of the two, so a bar's height is comparable across the pair instead of
  // each chart stretching its own tallest bar to fill the same space.
  const streakSpanSharedMax = Math.max(
    ...(streak_distribution || []).map((d) => d.count),
    ...(active_span_distribution || []).map((d) => d.count),
    1
  );

  const handleDownloadXlsx = () => {
    downloadSectionedXlsx("ltv.xlsx", "LTV", [
      {
        title:
          total_count > returned_count
            ? `Users (showing top ${formatNumber(returned_count)} of ${formatNumber(
                total_count
              )}, capped at ${formatNumber(row_limit)} — narrow filters for the rest)`
            : "Users",
        columns: [
          { header: "User", key: "provider_name" },
          { header: "Site", key: "site_name" },
          { header: "Station ID", key: "station_id" },
          { header: "Specialty", key: "specialty" },
          { header: "First Active", key: "first_active_month" },
          { header: "Last Active", key: "last_active_month" },
          { header: "Months Active", key: "months_active" },
          { header: "Longest Streak (mo)", key: "longest_streak_months" },
          { header: "Total Recordings", key: "total_recordings" },
          { header: "Currently Active", key: "currently_active" },
        ],
        rows: providers,
      },
      {
        title: "Summary",
        columns: [
          { header: "Users (filtered)", key: "total_count" },
          { header: "Avg Months Active", key: "avg_months_active" },
          { header: "Avg Longest Streak (mo)", key: "avg_longest_streak_months" },
          { header: "Currently Active %", key: "pct_currently_active", format: "percent" },
        ],
        rows: [summary],
      },
      {
        title: "Longest Streak Distribution",
        columns: [
          { header: "Streak (mo)", key: "value" },
          { header: "User Count", key: "count" },
        ],
        rows: streak_distribution || [],
      },
      {
        title: "Potential Streak Distribution",
        columns: [
          { header: "Active Span (mo)", key: "value" },
          { header: "User Count", key: "count" },
        ],
        rows: active_span_distribution || [],
      },
      {
        title: "Months Active Distribution",
        columns: [
          { header: "Months Active", key: "value" },
          { header: "User Count", key: "count" },
        ],
        rows: months_active_distribution || [],
      },
      {
        title: "Heatmap",
        columns: [
          { header: "VISN", key: "visn" },
          { header: "Specialty", key: "specialty" },
          { header: "User Count", key: "provider_count" },
          { header: "Avg Longest Streak (mo)", key: "avg_longest_streak_months" },
          { header: "Avg Months Active", key: "avg_months_active" },
          { header: "Currently Active %", key: "pct_currently_active", format: "percent" },
        ],
        rows: heatmap,
      },
    ]);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Customer LTV (beta)</h2>
          <p className="text-sm text-muted-foreground">
            Usage-based tenure and continuity per user, from their full recording history — not a dollar figure,
            and not scoped to the Report month filter (this looks at all-time activity). Months active counts distinct
            calendar months with at least one real recording; Longest streak is the longest run of{" "}
            <em>consecutive</em> active months; Currently active means their last recording was within the trailing 30
            days. Ranked by Months active, then Longest streak — the users who keep coming back the most, first.
          </p>
          {total_count > returned_count && (
            <p className="text-xs text-muted-foreground mt-1">
              Showing the top {formatNumber(returned_count)} of {formatNumber(total_count)} users with any recorded
              activity (capped at {formatNumber(row_limit)}). Narrow the filters to bring more into view.
            </p>
          )}
        </div>
        <Button variant="outline" onClick={handleDownloadXlsx}>
          Download XLSX
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Users (filtered)" value={formatNumber(summary.total_count)} />
        <KpiCard label="Avg months active" value={formatDecimal(summary.avg_months_active, 1)} />
        <KpiCard label="Avg longest streak" value={`${formatDecimal(summary.avg_longest_streak_months, 1)} mo`} />
        <KpiCard label="Currently active" value={formatPercent(summary.pct_currently_active)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Longest streak distribution</CardTitle>
            <CardDescription>
              One data point per user — their longest run of consecutive active months, for the current filters (
              {formatNumber(summary.total_count)} users).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!streak_distribution || streak_distribution.length === 0 ? (
              <p className="text-sm text-muted-foreground">No users with recorded activity match the selected filters.</p>
            ) : (
              <DistributionChart distribution={streak_distribution} unitLabel="mo" sharedMaxCount={streakSpanSharedMax} />
            )}
          </CardContent>
        </Card>

        {/* Comparison, side by side with the actual-streak chart: for each
            span length N, how many providers' first-active-to-last-active
            range is EXACTLY N months — the same "one bar per exact value"
            shape as the achieved-streak chart on the left, not a cumulative
            "at least N" count (a cumulative count would trivially show the
            entire population at N=1, since every provider has at least a
            1-month span by definition — not useful). Comparing the two
            charts bar-for-bar at the same N: if "potential" is much taller
            than "achieved," plenty of providers had that much calendar
            range but didn't sustain a streak that long without a gap. */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Potential streak (active span) distribution</CardTitle>
            <CardDescription>
              One data point per user — the span from their first active month to their last, for the current
              filters ({formatNumber(summary.total_count)} users).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!active_span_distribution || active_span_distribution.length === 0 ? (
              <p className="text-sm text-muted-foreground">No users with recorded activity match the selected filters.</p>
            ) : (
              <DistributionChart
                distribution={active_span_distribution}
                unitLabel="mo"
                sharedMaxCount={streakSpanSharedMax}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Months active distribution</CardTitle>
          <CardDescription>
            One data point per user — total distinct calendar months with at least one recording (not
            necessarily consecutive), for the current filters ({formatNumber(summary.total_count)} users).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!months_active_distribution || months_active_distribution.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users with recorded activity match the selected filters.</p>
          ) : (
            <DistributionChart distribution={months_active_distribution} unitLabel="mo" />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Loyalty by VISN and specialty</CardTitle>
          <CardDescription>
            Cell = average longest consecutive-month streak (darker = more loyal), with user count underneath.
            Hover a cell for the full breakdown.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {heatmap.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users with recorded activity match the selected filters.</p>
          ) : (
            <>
              <LoyaltyHeatmap heatmap={heatmap} />
              {unmapped_station_ids && unmapped_station_ids.length > 0 && (
                <p className="text-xs text-muted-foreground mt-3">
                  {UNMAPPED_VISN}: station{unmapped_station_ids.length === 1 ? "" : "s"} not found in the stations
                  table — {unmapped_station_ids.join(", ")}.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Returning users</CardTitle>
          <CardDescription>
            Ranked by months active, then longest consecutive streak
            {pager.total ? ` · top ${formatNumber(row_limit)} shown` : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {providers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users with recorded activity match the selected filters.</p>
          ) : (
            <>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Specialty</TableHead>
                  <TableHead>First active</TableHead>
                  <TableHead>Last active</TableHead>
                  <TableHead>Months active</TableHead>
                  <TableHead>Longest streak</TableHead>
                  <TableHead>Total recordings</TableHead>
                  <TableHead>Currently active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pager.pageRows.map((row) => (
                  <TableRow key={row.doctor_user_id}>
                    <TableCell>{row.provider_name || "—"}</TableCell>
                    <TableCell>
                      {row.site_name ? `${row.site_name} (${row.station_id})` : row.station_id || "—"}
                    </TableCell>
                    <TableCell>{row.specialty || "—"}</TableCell>
                    <TableCell>{formatMonth(row.first_active_month)}</TableCell>
                    <TableCell>{formatMonth(row.last_active_month)}</TableCell>
                    <TableCell>{formatNumber(row.months_active)}</TableCell>
                    <TableCell>{formatNumber(row.longest_streak_months)} mo</TableCell>
                    <TableCell>{formatNumber(row.total_recordings)}</TableCell>
                    <TableCell>
                      {row.currently_active ? (
                        <Badge variant="outline" className="text-green-600">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">Lapsed</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            <TablePager page={pager.page} totalPages={pager.totalPages} onPageChange={pager.setPage} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
